import React, { ReactElement } from 'react';

// Hooks
import {
    useActiveTiptapEditorCallback,
    useTiptapActiveEditorSelector,
} from '../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Utils
import {
    isListTypeClosestActive,
    hasNestedListContent,
} from '../../../../../../common/tiptap/extensions/list/tiptapListUtils';
import { findParentNode } from '@tiptap/react';

// Components
import RichTextTool from './RichTextTool';

// Types
import { ToolbarToolProps } from '../../../config/toolDefinitions/toolbarToolTypes';
import { TiptapNodeType } from '../../../../../../common/tiptap/tiptapTypes';

interface TiptapIndentControlProps extends ToolbarToolProps {
    command: 'sinkListItem' | 'liftListItem';
}

const TiptapTextIndentControlTool = ({ command, ...props }: TiptapIndentControlProps): ReactElement => {
    const isOrderListActive = useTiptapActiveEditorSelector(isListTypeClosestActive(TiptapNodeType.orderedList));
    const isBulletListActive = useTiptapActiveEditorSelector(isListTypeClosestActive(TiptapNodeType.bulletList));

    const isListActive = isOrderListActive || isBulletListActive;

    // Check if the operation is actually possible
    const canExecute = useTiptapActiveEditorSelector((editor) => {
        if (!editor || !isListActive) return false;

        // Check if the editor can sink/lift the list item
        // this is a way to check if the operation is possible
        return editor.can()?.[command]?.('listItem') || false;
    });

    const handleClick = useActiveTiptapEditorCallback((editor) => {
        if (!editor) return;

        // Early return for indent command - use standard approach
        if (command === 'sinkListItem') {
            return editor.commands?.[command]?.('listItem');
        }

        // Handle unindent (liftListItem) with nested content check
        const listItem = findParentNode((node) => node.type.name === TiptapNodeType.listItem)(editor.state.selection);

        if (listItem && hasNestedListContent(listItem.node)) {
            return editor.commands.liftListItemWithNestedContent();
        }

        // Fallback to standard command for simple lists as the custom command only handles nested content
        return editor.commands?.[command]?.('listItem');
    });

    return (
        <RichTextTool
            onClick={handleClick}
            iconName={props.iconName || 'toolbar-indent'}
            disabled={!canExecute}
            {...props}
        />
    );
};

export default TiptapTextIndentControlTool;
